<script>
import BookingDaysTable from '@/components/booking/bookingList/side-panel/booking-days-table.vue'
import BookingDetailHeader from '@/components/booking/bookingList/side-panel/booking-detail-header.vue'
import BookingDetailsTab from '@/components/booking/bookingList/side-panel/booking-details-tab.vue'
import BookingEducatorsTab from '@/components/booking/bookingList/side-panel/booking-educators-tab.vue'
import baseMixin from '@/mixins/baseMixin'
import featureFlagMixin from '@/mixins/featureFlagMixin'
import dayjs from '@/services/date'
import BookingStatusLabelType from '@/shared/constants/booking/BookingStatusLabelType'
import ClientBookingStatus from '@/shared/constants/booking/ClientBookingStatus'
import BookingType from '@/shared/constants/booking/BookingType'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import routeDefinitions from '@/shared/constants/routes/routeDefinitions'
import { mapGetters } from 'vuex'
import CustomTooltip from '../custom-tooltip.vue'

const BookingTabs = {
  Details: 0,
  Days: 1,
  Educators: 2,
}

export default {
  name: 'BookingSidePanel',
  components: {
    BookingDetailHeader,
    BookingDetailsTab,
    BookingEducatorsTab,
    BookingDaysTable,
    CustomTooltip,
  },
  mixins: [baseMixin, featureFlagMixin],
  props: {
    value: { type: Boolean, default: false },
    booking: { type: Object, default: null },
  },
  data() {
    return {
      tab: BookingTabs.Details,
      educatorsTab: BookingTabs.Educators,
      BookingStatusLabelType,
      BookingType,
    }
  },
  computed: {
    ...mapGetters({
      getSelectedClients: 'client/getSelectedClients',
      hasCreateBookingPermissionForAtleastOneClientLocationByClientIds:
        'auth/hasCreateBookingPermissionForAtleastOneClientLocationByClientIds',
    }),
    visible: {
      get() {
        return this.value
      },
      set(v) {
        this.tab = BookingTabs.Details
        this.$emit('input', v)
      },
    },
    drawerWidth() {
      return this.$vuetify &&
        this.$vuetify.breakpoint &&
        this.$vuetify.breakpoint.smAndDown
        ? '100%'
        : 720
    },
    bookingTabs() {
      const { Details, Days, Educators } = BookingTabs

      return [
        {
          id: Details,
          label: this.$t('sidePanel.details'),
        },

        {
          id: Days,
          label: `${this.$t('sidePanel.days')} (${this.daysCount})`,
        },
        {
          id: Educators,
          label: this.$t('sidePanel.educators'),
          hidden: !this.isMultiTentative,
        },
      ].filter(({ hidden = false }) => !hidden)
    },
    bookingTabItems() {
      const { Details, Days, Educators } = BookingTabs

      return [
        {
          id: Details,
          component: BookingDetailsTab,
          props: {
            booking: this.booking,
            display: this._display,
          },
        },
        {
          id: Days,
          component: BookingDaysTable,
          props: {
            blocks: this._blocks,
            display: this._display,
            bookingId: this.booking ? this.booking.id : 0,
          },
        },
        {
          id: Educators,
          component: BookingEducatorsTab,
          props: {
            booking: this.booking,
            isActive: this.tab === Educators,
          },
          hidden: !this.isMultiTentative,
        },
      ].filter(({ hidden = false }) => !hidden)
    },
    _groupMode() {
      return !!(
        this.booking &&
        this.booking.groupId &&
        this.booking.groupId !== 0
      )
    },

    // normalized blocks (either blockDetails or single outer booking)
    _blocks() {
      if (!this.booking) return []
      if (this._groupMode) {
        const arr = Array.isArray(this.booking.blockDetails)
          ? this.booking.blockDetails
          : []

        return arr
          .filter(
            (blockBooking) =>
              blockBooking &&
              blockBooking.startDateTimeLocal &&
              blockBooking.endDateTimeLocal
          )
          .map((blockBooking) => ({
            id: blockBooking.id,
            start: new Date(blockBooking.startDateTimeLocal),
            end: new Date(blockBooking.endDateTimeLocal),
            candidate: blockBooking.candidate || null,
            tentativeCandidates: this.getTentativeCandidateDetailsForBlock(
              blockBooking
            ),
            status: blockBooking.status,
            statusLabel: blockBooking.statusLabel,
            isToday:
              this.canCreateBookings &&
              blockBooking.statusLabel === BookingStatusLabelType.Open &&
              dayjs(blockBooking.startDateTimeLocal).format(
                DateFormatToken.YYYYMMDD
              ) ===
                dayjs()
                  .tz(this.booking.location.timeZone)
                  .format(DateFormatToken.YYYYMMDD),
            canEdit:
              this.canCreateBookings &&
              blockBooking.statusLabel === BookingStatusLabelType.Open &&
              dayjs(blockBooking.startDateTimeLocal).format(
                DateFormatToken.YYYYMMDD
              ) >
                dayjs()
                  .tz(this.booking.location.timeZone)
                  .format(DateFormatToken.YYYYMMDD),
          }))
          .sort((a, b) => a.start - b.start)
      }
      // single
      return [
        {
          id: this.booking.id,
          start: this.booking.startDateTimeLocal
            ? new Date(this.booking.startDateTimeLocal)
            : null,
          end: this.booking.endDateTimeLocal
            ? new Date(this.booking.endDateTimeLocal)
            : null,
          candidate: this.booking.candidate || null,
          tentativeCandidates: this.booking?.tentativeCandidates || [],
          status: this.booking.status,
          statusLabel: this.booking.statusLabel,
          isToday:
            this.canCreateBookings &&
            this.booking.statusLabel === BookingStatusLabelType.Open &&
            this.isSingleBookingSameDay,
          canEdit:
            this.canCreateBookings &&
            this.booking.statusLabel === BookingStatusLabelType.Open &&
            dayjs(this.booking.startDateTimeLocal).format(
              DateFormatToken.YYYYMMDD
            ) >
              dayjs()
                .tz(this.booking.location.timeZone)
                .format(DateFormatToken.YYYYMMDD),
        },
      ].filter((b) => b.start && b.end)
    },

    daysCount() {
      return this._groupMode ? this._blocks.length : this._blocks.length ? 1 : 0
    },
    canCreateBookings() {
      return this.hasCreateBookingPermissionForAtleastOneClientLocationByClientIds(
        this.getSelectedClients
      )
    },
    isBlockEdit() {
      if (!this.booking) return false

      const hasBlocks =
        this.booking.blockDetails &&
        Array.isArray(this.booking.blockDetails) &&
        this.booking.blockDetails.length > 0

      const allBlocksOpenAndFuture =
        hasBlocks &&
        this.booking.blockDetails.every(
          (block) =>
            block.statusLabel === BookingStatusLabelType.Open &&
            dayjs(block.startDateTimeLocal).format(DateFormatToken.YYYYMMDD) >
              dayjs()
                .tz(this.booking.location.timeZone)
                .format(DateFormatToken.YYYYMMDD)
        )

      const bookingCount = hasBlocks ? this.booking.blockDetails.length : 0

      return (
        this.canCreateBookings &&
        this.booking.statusLabel === BookingStatusLabelType.Open &&
        allBlocksOpenAndFuture &&
        bookingCount === this.booking.openCount
      )
    },
    isDuplicate() {
      if (!this.booking) return false

      const validStatuses = [
        BookingStatusLabelType.Open,
        BookingStatusLabelType.Pending,
        BookingStatusLabelType.Unapproved,
        BookingStatusLabelType.Confirmed,
      ]

      let isValidStatus = false

      const hasBlocks =
        Array.isArray(this.booking.blockDetails) &&
        this.booking.blockDetails.length > 0

      if (this.canCreateBookings) {
        if (hasBlocks) {
          isValidStatus =
            hasBlocks &&
            this.booking.blockDetails.every((block) =>
              validStatuses.includes(block.statusLabel)
            )
        } else {
          isValidStatus = validStatuses.includes(this.booking.statusLabel)
        }
      }

      return this.canCreateBookings && isValidStatus
    },
    isSingleDayEdit() {
      if (!this.booking) return false

      return (
        this.canCreateBookings &&
        this.booking.statusLabel === BookingStatusLabelType.Open &&
        this.booking.blockDetails === null
      )
    },
    isSingleBookingSameDay() {
      return (
        dayjs(this.booking.startDateTimeLocal).format(
          DateFormatToken.YYYYMMDD
        ) ===
        dayjs()
          .tz(this.booking.location.timeZone)
          .format(DateFormatToken.YYYYMMDD)
      )
    },
    isSingleBookingSameorPastDay() {
      return (
        dayjs(this.booking.startDateTimeLocal).format(
          DateFormatToken.YYYYMMDD
        ) <=
        dayjs()
          .tz(this.booking.location.timeZone)
          .format(DateFormatToken.YYYYMMDD)
      )
    },
    canDuplicateBookings() {
      return this.selfServiceFeatureFlags.enableDuplicateBooking
    },
    canEditBookings() {
      return (
        this.selfServiceFeatureFlags.enableEditBooking &&
        (this.isSingleDayEdit || this.isBlockEdit)
      )
    },
    disclaimerText() {
      const location = this.booking?.location?.name
      const client = this.booking?.client?.name

      const subject = [
        client && client !== location && `for ${client}`,
        location && `at ${location}`,
      ]
        .filter(Boolean)
        .join(' ')

      return this.$t('booking.details.disclaimer', { subject })
    },
    isMultiTentative() {
      return this.booking?.status === ClientBookingStatus.multiTentative
    },
  },
  methods: {
    // display fallback
    _display(v) {
      if (v === null || v === undefined) return '-'
      if (typeof v === 'string' && v.trim() === '') return '-'
      return v
    },
    editBookingBlock() {
      this.$router.push({
        name: routeDefinitions.bookingsEdit.name,
        params: { bookingId: this.booking.id },
        query: {
          mode: BookingType.Block,
        },
      })
    },
    editBookingSingleDay(date) {
      this.$router.push({
        name: routeDefinitions.bookingsEdit.name,
        params: { bookingId: this.booking.id },
        query: {
          mode: BookingType.SingleDay,
          date: dayjs(date).format(DateFormatToken.YYYYMMDD),
        },
      })
    },
    handleEditClick(date) {
      if (this.isSingleDayEdit) {
        if (this.isSingleBookingSameorPastDay) return

        return this.editBookingSingleDay(date)
      }
      return this.editBookingBlock()
    },
    duplicateBooking() {
      const hasBlocks =
        Array.isArray(this.booking.blockDetails) &&
        this.booking.blockDetails.length > 0

      const bookingMode = hasBlocks ? BookingType.Block : BookingType.SingleDay

      const query = {
        mode: bookingMode,
      }

      if (!hasBlocks) {
        query.date = dayjs(this.booking.startDateTimeLocal).format(
          DateFormatToken.YYYYMMDD
        )
      }

      this.$router.push({
        name: routeDefinitions.bookingsDuplicate.name,
        params: { bookingId: this.booking.id },
        query,
      })
    },
    refreshEducators() {
      if (!this.$refs.bookingEducatorsTab) return

      this.$refs.bookingEducatorsTab[0].refresh()
    },
    getTentativeCandidateDetailsForBlock(blockBooking) {
      if (!blockBooking?.tentativeCandidateIds?.length) return null

      return blockBooking.tentativeCandidateIds.map((candidateId) => {
        const candidateDetails = this.booking?.tentativeCandidates?.find(
          ({ id }) => id === candidateId
        )

        return (
          candidateDetails ?? {
            id: candidateId,
            firstName: this.$t('booking.defaultEducatorName'),
            lastName: '',
          }
        )
      })
    },
  },
}
</script>

<template>
  <v-navigation-drawer
    v-model="visible"
    app
    fixed
    right
    temporary
    overlay-color="rgba(0,0,0,.35)"
    :width="drawerWidth"
    class="booking-side-panel"
  >
    <div class="close-btn">
      <div class="action-btn">
        <BaseButton
          v-if="canDuplicateBookings"
          icon="mdi-content-copy"
          fab
          tertiary
          circle
          sm
          hover-color="grey lighten-4"
          :class="isDuplicate ? 'panel-copy' : 'panel-copy-disabled'"
          @click="isDuplicate && duplicateBooking()"
        />
        <CustomTooltip
          position="left"
          :text="
            isSingleDayEdit
              ? isSingleBookingSameDay
                ? $t('booking.editTodayBookingWarning')
                : $t('booking.editBooking')
              : $t('booking.editBookingBlock')
          "
        >
          <BaseButton
            v-if="canEditBookings"
            icon="mdi-pencil-outline"
            fab
            tertiary
            circle
            sm
            hover-color="grey lighten-4"
            :class="
              isSingleDayEdit && isSingleBookingSameorPastDay
                ? 'panel-edit-disabled'
                : 'panel-edit'
            "
            :data-button="`booking-edit-${booking.id}`"
            @click="handleEditClick(booking.startDateTimeLocal)"
          />
        </CustomTooltip>
      </div>
      <BaseButton
        icon="mdi-close"
        fab
        tertiary
        circle
        sm
        hover-color="grey lighten-4"
        class="panel-close"
        @click="$emit('input', false)"
      />
    </div>

    <v-divider class="panel-divider"></v-divider>

    <BookingDetailHeader
      :booking="booking"
      :blocks="_blocks"
      :group-mode="_groupMode"
      :display="_display"
    />

    <v-divider class="panel-divider"></v-divider>

    <div class="panel-tabs-row px-6">
      <v-tabs
        v-model="tab"
        show-arrows
        slider-color="#00ACEF"
        color="#E0E8EE"
        class="panel-tabs"
      >
        <v-tab v-for="{ id, label } in bookingTabs" :key="id" class="panel-tab">
          {{ label }}
        </v-tab>
      </v-tabs>

      <transition name="fade">
        <v-btn
          v-if="tab === educatorsTab"
          outlined
          prepend-icon="mdi-refresh"
          color="primary"
          class="rounded-lg mb-2"
          @click="refreshEducators"
        >
          <v-icon class="mr-2" small color="primary">mdi-refresh</v-icon>
          {{ $t('general.refresh') }}
        </v-btn>
      </transition>
    </div>

    <v-tabs-items v-model="tab" class="px-6">
      <v-tab-item v-for="{ id, component, props } in bookingTabItems" :key="id">
        <component
          :is="component"
          :ref="id === educatorsTab ? 'bookingEducatorsTab' : null"
          v-bind="props"
        />
      </v-tab-item>
    </v-tabs-items>

    <p v-if="disclaimerText" class="pl-6 pr-10 mt-8 text-caption font-italic">
      {{ disclaimerText }}
    </p>
  </v-navigation-drawer>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/_vue-transitions.scss';
@import '@/styles/bookingList/side-panel.scss';
</style>
